import type { TranslationKey } from "@privacy/translations";
import type { ComponentType, FC, ReactNode } from "react";
import { useState } from "react";
import { useFormContext, Controller, useForm, useController, useWatch } from "react-hook-form";
import {
  Box,
  Stack,
  RadioGroup,
  FormControlLabel,
  Autocomplete,
  TextField,
  IconButton,
  Tooltip,
  type AutocompleteRenderGroupParams,
} from "@mui/material";
import { Radio } from "@privacy/ui";
import { useTranslation } from "@privacy/translations";
import { useMutation, useQuery } from "@tanstack/react-query";
import { XIcon } from "../../shared/icons";
import { logError } from "../../api/logError";
import type { GetSelectPartnerOptionsResponse, PartnerRole } from "../../api/partners";
import { selectPartnerOptionsQueryOptions } from "../../api/partners";
import type { InputProps } from "../DynamicInput";
import * as FormDialog from "../../shared/FormDialog";
import { ErrorMessage } from "../inlineForm/ErrorMessage";
import { useDynamicFormState } from "../DynamicFormProvider";
import { PartnerIcon3, TenantIcon3, type IconProps } from "../../shared/icons";
import { useCurrentCustomer, useCurrentOrganisation } from "../../root/CurrentOrganisationProvider";
import type { UserInfoCustomer, UserInfoOrganisation } from "../../api/userInfo";
import { customerOrganisationNames, isEnterpriseCustomerScope } from "../../enterpriseCustomer/enterpriseCustomer";
import {
  enterpriseCustomerOrganisationsQueryOptions,
  type EnterpriseCustomerOrganisation,
} from "../../api/enterpriseCustomer";
import {
  personalDataControllersDisplayListQueryOptions,
  personalDataControllersListQueryOptions,
  type PersonalDataControllerListItem,
} from "../../api/personalDataControllers";
import type { MultiplePartnerType, MultiplePartnerValue } from "../types";
import type { AutocompleteOptionType } from "../../shared/Autocomplete";
import {
  AutocompleteOption,
  createFilterAutocompleteOption,
  CreateNewAutocompleteOption,
} from "../../shared/Autocomplete";
import { CreatePartnerDialog } from "../../pages/PartnersGridPage/CreatePartnerDialog";
import { useGetSortByKey } from "../../shared/SortByKey";
import { usePermission } from "../../shared/permissions";
import { DynamicShared } from "../../dynamicShared";
import { isAnswerRequestEmpty } from "./isAnswerRequestEmpty";
import { AddDialogTrigger } from "./AddDialogTrigger";

type MultiplePartnerInputBaseProps = InputProps<MultiplePartnerValue> & {
  header: string;
  isMultistepFormVersion: boolean;
};
export const MultiplePartnerInputBase: FC<MultiplePartnerInputBaseProps> = ({
  questionCard,
  onSave,
  readonly,
  header,
  isMultistepFormVersion,
}) => {
  const { mandatoryQuestionErrorMessage } = useDynamicFormState();
  const currentCustomer = useCurrentCustomer();
  const currentOrganisation = useCurrentOrganisation();
  const selectPartnerOptionsQuery = useQuery(selectPartnerOptionsQueryOptions());
  const personalDataControllersDisplayListQuery = useQuery(personalDataControllersDisplayListQueryOptions());
  const enterpriseCustomerOrganisationsQuery = useQuery({
    ...enterpriseCustomerOrganisationsQueryOptions(),
    enabled: availablePartnerTypes({ currentCustomer, currentOrganisation }).includes("customerOrganisation"),
  });
  const { control } = useFormContext();
  const saveMutation = useMutation({ mutationFn: (value: MultiplePartnerValue) => onSave(value) });

  const { field } = useController({ control, name: questionCard.id });
  const fieldValue = (field.value ?? []) as MultiplePartnerValue;

  const addPartner = (partnerToAdd: MultiplePartnerValue[number]) => {
    const newValue = [...fieldValue, partnerToAdd];
    return saveMutation.mutateAsync(newValue, { onSuccess: () => field.onChange(newValue) });
  };

  const removePartner = (partnerToRemove: MultiplePartnerValue[number]) => {
    const newValue = fieldValue.filter(
      partner => !(partner.id === partnerToRemove.id && partner.type === partnerToRemove.type)
    );
    field.onChange(newValue);
    return saveMutation.mutateAsync(newValue);
  };

  return (
    <>
      <Stack sx={{ mb: 0.5 }}>
        {fieldValue.map(entity => {
          if (!selectPartnerOptionsQuery.data || !personalDataControllersDisplayListQuery.data) return null;

          const partnerInfo = getPartnerInfo(entity, {
            selectPartnerOptions: selectPartnerOptionsQuery.data,
            personalDataControllers: personalDataControllersDisplayListQuery.data,
            enterpriseCustomerOrganisations:
              enterpriseCustomerOrganisationsQuery.data ?? customerOrganisationNames(currentCustomer),
          });

          if (!partnerInfo) return null;

          return (
            <Item
              key={`${entity.id}-${entity.type}`}
              readonly={readonly}
              name={partnerInfo.name}
              onRemoveClick={() => removePartner(entity)}
              Icon={partnerInfo.Icon}
            />
          );
        })}

        <Box>
          <AddDialogTrigger
            renderDialog={({ onClose }) => (
              <PartnerModal
                onClose={onClose}
                onSubmit={addPartner}
                title={header}
                selectedPartners={fieldValue}
                partnerRole={
                  questionCard.input.type === "multiplePartnerInput" ? questionCard.input.partnerRole : undefined
                }
              />
            )}
            isMultistepFormVersion={isMultistepFormVersion}
            isEmpty={fieldValue.length === 0}
            isReadOnly={readonly}
          >
            {`+ ${header}`}
          </AddDialogTrigger>
          {saveMutation.isError && <ErrorMessage />}
          {mandatoryQuestionErrorMessage && questionCard.mandatory && isAnswerRequestEmpty(field.value) && (
            <ErrorMessage variant="warning" errorMessage={mandatoryQuestionErrorMessage} />
          )}
        </Box>
      </Stack>
    </>
  );
};

type PartnerInfo = { name: string; Icon: ComponentType<IconProps> };

type PartnerEntityLists = {
  selectPartnerOptions: GetSelectPartnerOptionsResponse | undefined;
  personalDataControllers: Array<PersonalDataControllerListItem> | undefined;
  enterpriseCustomerOrganisations: Array<Pick<EnterpriseCustomerOrganisation, "id" | "organizationName">> | undefined;
};

const PARTNER_TYPES: Record<
  MultiplePartnerType,
  {
    labelKey: TranslationKey;
    noOptionsTextKey: TranslationKey;
    placeholderKey: TranslationKey;
    Icon: ComponentType<IconProps>;
    entities: (lists: PartnerEntityLists) => Array<{ id: string; name: string }> | undefined;
    notFoundMessage: (id: string) => string;
  }
> = {
  partner: {
    labelKey: "DynamicForm.MultiplePartnerInput.Partner",
    noOptionsTextKey: "DynamicForm.MultiplePartnerInput.Partner.Empty",
    placeholderKey: "DynamicForm.MultiplePartnerInput.Partner.Placeholder",
    Icon: PartnerIcon3,
    entities: ({ selectPartnerOptions }) => selectPartnerOptions,
    notFoundMessage: id => `Partner for id: ${id} not found`,
  },
  personalDataController: {
    labelKey: "DynamicForm.MultiplePartnerInput.PersonalDataController",
    noOptionsTextKey: "DynamicForm.MultiplePartnerInput.PersonalDataController.Empty",
    placeholderKey: "DynamicForm.MultiplePartnerInput.PersonalDataController.Placeholder",
    Icon: TenantIcon3,
    entities: ({ personalDataControllers }) => personalDataControllers,
    notFoundMessage: id => `Personal data controller for id: ${id} not found`,
  },
  customerOrganisation: {
    labelKey: "DynamicForm.MultiplePartnerInput.CustomerOrganisation",
    noOptionsTextKey: "DynamicForm.MultiplePartnerInput.CustomerOrganisation.Empty",
    placeholderKey: "DynamicForm.MultiplePartnerInput.CustomerOrganisation.Placeholder",
    Icon: TenantIcon3,
    entities: ({ enterpriseCustomerOrganisations }) =>
      enterpriseCustomerOrganisations?.map(({ id, organizationName }) => ({ id, name: organizationName })),
    notFoundMessage: id => `Enterprise customer organisation for id: ${id} not found`,
  },
};

const availablePartnerTypes = ({
  currentCustomer,
  currentOrganisation,
}: {
  currentCustomer: UserInfoCustomer;
  currentOrganisation: UserInfoOrganisation;
}): Array<MultiplePartnerType> => {
  if (currentCustomer.customerKind !== "enterprise") return ["partner", "personalDataController"];
  if (isEnterpriseCustomerScope({ currentCustomer, currentOrganisation })) return ["partner", "customerOrganisation"];
  return ["partner"];
};

export const getPartnerInfo = (
  selected: MultiplePartnerValue[number],
  lists: PartnerEntityLists
): PartnerInfo | null => {
  const { entities, notFoundMessage, Icon } = PARTNER_TYPES[selected.type];
  const candidates = entities(lists);

  if (!candidates) {
    logError(`Partner info not handled for ${JSON.stringify(selected)}`);
    return null;
  }

  const entity = candidates.find(candidate => candidate.id === selected.id);

  if (!entity) {
    logError(notFoundMessage(selected.id));
    return null;
  }

  return { name: entity.name, Icon };
};

type ItemProps = {
  name: string;
  Icon: ComponentType<IconProps>;
  onRemoveClick: () => void;
  readonly: boolean;
};

const ACTION_BUTTONS_CLASS_NAME = "action-buttons";

const Item: FC<ItemProps> = ({ name, onRemoveClick, readonly, Icon }) => {
  const { t } = useTranslation();
  return (
    <Stack
      sx={{
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
        borderRadius: 1,
        py: 0.2,
        px: 2,
        ...(!readonly && {
          "&:hover": { backgroundColor: theme => theme.palette.grey["100"] },
          [`&:hover .${ACTION_BUTTONS_CLASS_NAME}`]: { visibility: "visible" },
        }),
      }}
    >
      <DynamicShared.Chip label={name} variant="entity" renderIcon={props => <Icon {...props} />} />
      <Stack direction="row" className={ACTION_BUTTONS_CLASS_NAME} sx={{ visibility: "hidden" }}>
        <Tooltip title={t("DynamicForm.MultiplePartnerInput.Remove")}>
          <IconButton aria-label={t("DynamicForm.MultiplePartnerInput.Remove")} onClick={onRemoveClick}>
            <XIcon size={14} />
          </IconButton>
        </Tooltip>
      </Stack>
    </Stack>
  );
};

type PartnerModalProps = {
  onClose: () => void;
  onSubmit: (value: MultiplePartnerValue[number]) => Promise<unknown>;
  title: string;
  selectedPartners: MultiplePartnerValue;
  partnerRole?: PartnerRole;
};

type FormValues = {
  type: MultiplePartnerType;
  selectedOption: AutocompleteOptionType | null;
};

const PARTNER_GROUPS = { matchingRole: "matchingRole", otherRole: "otherRole" } as const;

type PartnerGroup = (typeof PARTNER_GROUPS)[keyof typeof PARTNER_GROUPS];

type PartnerOption = AutocompleteOptionType & { group?: PartnerGroup; isNew?: boolean; inputValue?: string };

type CreateNewDialogProps = {
  onClose: () => void;
  onCreated: (created: { id: string; name: string }) => void;
  defaultValue?: string;
};

type PartnerTypeSelectConfig = {
  options: Array<PartnerOption>;
  groupBy?: (option: PartnerOption) => string;
  renderGroup?: (params: AutocompleteRenderGroupParams) => ReactNode;
  renderCreateNewDialog?: (props: CreateNewDialogProps) => ReactNode;
};

const partnerGroup = (option: GetSelectPartnerOptionsResponse[number], partnerRole?: PartnerRole) => {
  if (!partnerRole) return undefined;

  return option.roles.includes(partnerRole) ? PARTNER_GROUPS.matchingRole : PARTNER_GROUPS.otherRole;
};

const RADIO_Y_SPACING = 1;
const RADIO_X_SPACING = 2;

const PartnerModal: FC<PartnerModalProps> = ({ onClose, onSubmit, title, selectedPartners, partnerRole }) => {
  const { t } = useTranslation();
  const currentCustomer = useCurrentCustomer();
  const currentOrganisation = useCurrentOrganisation();
  const availableTypes = availablePartnerTypes({ currentCustomer, currentOrganisation });
  const isTypeSelectionVisible = availableTypes.length > 1;
  const personalDataControllersListQuery = useQuery({
    ...personalDataControllersListQueryOptions(),
    enabled: availableTypes.includes("personalDataController"),
  });
  const enterpriseCustomerOrganisationsQuery = useQuery({
    ...enterpriseCustomerOrganisationsQueryOptions(),
    enabled: availableTypes.includes("customerOrganisation"),
  });
  const selectPartnerOptionsQuery = useQuery(selectPartnerOptionsQueryOptions());
  const sortByKey = useGetSortByKey();
  const [createNewDialogState, setCreateNewDialogState] = useState<
    { isOpen: true; inputValue: string } | { isOpen: false }
  >({
    isOpen: false,
  });
  const { hasPermission } = usePermission();
  const { handleSubmit, control, setFocus, resetField } = useForm<FormValues>({
    defaultValues: { type: "partner", selectedOption: null },
  });
  const submitMutation = useMutation({ mutationFn: onSubmit });
  const selectedType = useWatch({ control, name: "type" });

  const partnerOptions: Array<AutocompleteOptionType & { group?: PartnerGroup }> = (
    selectPartnerOptionsQuery.data ?? []
  ).map(option => ({
    label: option.name,
    value: option.id,
    group: partnerGroup(option, partnerRole),
  }));

  const sortedPartnerOptions = sortByKey(partnerOptions, "label");

  const matchingRolePartnerOptions = sortedPartnerOptions.filter(
    option => option.group === PARTNER_GROUPS.matchingRole
  );
  const otherRolePartnerOptions = sortedPartnerOptions.filter(
    option => option.group === PARTNER_GROUPS.otherRole || !option.group
  );
  const shouldGroupPartnerOptions =
    partnerOptions.length > 10 && matchingRolePartnerOptions.length > 0 && otherRolePartnerOptions.length > 0;

  const sortedPersonalDataControllers = sortByKey(
    (personalDataControllersListQuery.data ?? []).map(({ id, name }) => ({ label: name, value: id })),
    "label"
  );

  const selectableOrganisations = sortByKey(
    (enterpriseCustomerOrganisationsQuery.data ?? [])
      .filter(organisation => organisation.deletedDate === null)
      .map(({ id, organizationName }) => ({ label: organizationName, value: id })),
    "label"
  );

  const selectConfigByType: Record<MultiplePartnerType, PartnerTypeSelectConfig> = {
    partner: {
      options: shouldGroupPartnerOptions
        ? [...matchingRolePartnerOptions, ...otherRolePartnerOptions]
        : sortedPartnerOptions,
      groupBy: shouldGroupPartnerOptions ? option => option.group ?? "" : undefined,
      renderGroup: partnerRole
        ? params => (
            <li key={params.key}>
              {params.group === PARTNER_GROUPS.otherRole && (
                <Box sx={{ height: "1px", backgroundColor: theme => theme.palette.divider }} />
              )}
              <Box component="ul" sx={{ p: 0 }}>
                {params.children}
              </Box>
            </li>
          )
        : undefined,
      renderCreateNewDialog: ({ onClose, onCreated, defaultValue }) => (
        <CreatePartnerDialog onClose={onClose} onCreated={onCreated} defaultName={defaultValue} />
      ),
    },
    personalDataController: { options: sortedPersonalDataControllers },
    customerOrganisation: { options: selectableOrganisations },
  };

  FormDialog.useAutoFocusField(setFocus, isTypeSelectionVisible ? "type" : null);

  return (
    <FormDialog.Dialog isOpen={true}>
      <FormDialog.Title>{title}</FormDialog.Title>
      <FormDialog.Form
        onSubmit={handleSubmit(({ type, selectedOption }) => {
          if (!selectedOption) return;
          submitMutation.mutate({ type, id: selectedOption.value }, { onSuccess: onClose });
        })}
      >
        <FormDialog.Content>
          <FormDialog.FormError isError={submitMutation.isError} />
          <FormDialog.Fields>
            {isTypeSelectionVisible && (
              <Controller
                control={control}
                name="type"
                rules={{ required: true }}
                render={({ field }) => (
                  <RadioGroup
                    id="processor-type"
                    value={field.value}
                    onChange={e => {
                      field.onChange(e.target.value);
                      resetField("selectedOption");
                    }}
                  >
                    {availableTypes.map(type => (
                      <RadioWithLabel key={type} value={type} labelKey={PARTNER_TYPES[type].labelKey} />
                    ))}
                  </RadioGroup>
                )}
              />
            )}

            <Controller
              control={control}
              name="selectedOption"
              rules={{ required: true }}
              render={({ field, fieldState }) => {
                const { labelKey, noOptionsTextKey, placeholderKey, Icon } = PARTNER_TYPES[selectedType];
                const { options, groupBy, renderGroup, renderCreateNewDialog } = selectConfigByType[selectedType];
                const value: PartnerOption | null = field.value ?? null;

                return (
                  <FormDialog.Field errorMessage={fieldState.error && t("Input.Validation.Required")}>
                    {!isTypeSelectionVisible && (
                      <FormDialog.Label htmlFor={selectedType}>{t(labelKey)}</FormDialog.Label>
                    )}
                    {createNewDialogState.isOpen &&
                      renderCreateNewDialog?.({
                        onClose: () => setCreateNewDialogState({ isOpen: false }),
                        onCreated: ({ id, name }) => field.onChange({ value: id, label: name }),
                        defaultValue: createNewDialogState.inputValue,
                      })}
                    <Autocomplete
                      key={`${selectedType}-${shouldGroupPartnerOptions}`}
                      noOptionsText={t(noOptionsTextKey)}
                      {...field}
                      value={value}
                      onChange={(_, selectedOption) => {
                        if (selectedOption?.isNew)
                          return setCreateNewDialogState({ isOpen: true, inputValue: selectedOption.inputValue ?? "" });
                        field.onChange(selectedOption);
                      }}
                      openOnFocus
                      clearOnBlur
                      clearOnEscape
                      disableClearable={Boolean(value)}
                      blurOnSelect
                      id={selectedType}
                      options={options}
                      getOptionLabel={option => option.label}
                      isOptionEqualToValue={(option, selected) => option.value === selected.value}
                      getOptionDisabled={option => selectedPartners.some(partner => partner.id === option.value)}
                      groupBy={groupBy}
                      renderGroup={renderGroup}
                      filterOptions={(sourceOptions, params) => {
                        const filtered = createFilterAutocompleteOption(sourceOptions, params);

                        if (renderCreateNewDialog && hasPermission("nova-forms-api:partner:create")) {
                          return [
                            ...filtered,
                            {
                              value: "",
                              inputValue: params.inputValue,
                              label: params.inputValue
                                ? t("MultiAutocomplete.CreateNewOption.Label", { inputValue: params.inputValue })
                                : t("MultiAutocomplete.CreateNewOption.No.Input.Label"),
                              isNew: true,
                              group: partnerRole ? PARTNER_GROUPS.otherRole : undefined,
                            },
                          ];
                        }

                        return filtered;
                      }}
                      renderOption={(props, option) => {
                        const { key, ...optionProps } = props;
                        return option.isNew ? (
                          <CreateNewAutocompleteOption key={option.value} {...optionProps} option={option} />
                        ) : (
                          <AutocompleteOption key={option.value} {...optionProps} option={option} dense />
                        );
                      }}
                      renderInput={params => (
                        <TextField
                          {...params}
                          size="small"
                          inputRef={field.ref}
                          error={Boolean(fieldState.error)}
                          placeholder={t(placeholderKey)}
                          slotProps={{
                            ...params.slotProps,
                            input: { ...params.slotProps.input, startAdornment: field.value && <Icon /> },
                            htmlInput: {
                              ...params.slotProps.htmlInput,
                              ...(isTypeSelectionVisible && { "aria-label": t(labelKey) }),
                            },
                          }}
                        />
                      )}
                    />
                  </FormDialog.Field>
                );
              }}
            />
          </FormDialog.Fields>
        </FormDialog.Content>

        <FormDialog.ActionButtons>
          <FormDialog.CancelButton onClick={onClose} />
          <FormDialog.SubmitButton isSubmitting={submitMutation.isPending}>
            {t("DynamicForm.MultiplePartnerInput.AddButton")}
          </FormDialog.SubmitButton>
        </FormDialog.ActionButtons>
      </FormDialog.Form>
    </FormDialog.Dialog>
  );
};

const RadioWithLabel: FC<{ value: string; labelKey: TranslationKey }> = ({ value, labelKey }) => {
  const { t } = useTranslation();
  return (
    <FormControlLabel
      slotProps={{ typography: { sx: { fontSize: theme => theme.typography.pxToRem(14) } } }}
      value={value}
      control={<Radio size="small" sx={{ py: RADIO_Y_SPACING, px: RADIO_X_SPACING }} />}
      label={t(labelKey)}
    />
  );
};
